export const LIMITE_SOBREDIMENSIONAMENTO = 0.15;

export const normalizarPotenciaKit = (valor) => {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : NaN;
  if (typeof valor === 'string') {
    const numero = Number(valor.replace(',', '.').trim());
    return Number.isFinite(numero) ? numero : NaN;
  }
  return NaN;
};

export const classificarAdequacaoKit = ({
  potenciaNecessaria,
  potenciaKit,
  compatibilidadeParcial = false,
  semKit = false,
  limite = LIMITE_SOBREDIMENSIONAMENTO,
}) => {
  const necessaria = normalizarPotenciaKit(potenciaNecessaria);
  const kit = normalizarPotenciaKit(potenciaKit);

  if (semKit || !Number.isFinite(kit) || kit <= 0) {
    return {
      status: 'sem_kit',
      bloqueia: true,
      potenciaNecessaria: Number.isFinite(necessaria) ? necessaria : 0,
      potenciaKit: null,
      percentual: null,
      titulo: 'Nenhum kit compatível encontrado',
      mensagem: 'Cadastre um kit compatível para continuar com a proposta.',
    };
  }

  if (!Number.isFinite(necessaria) || necessaria <= 0) {
    return {
      status: 'indefinido',
      bloqueia: false,
      potenciaNecessaria: 0,
      potenciaKit: kit,
      percentual: null,
      titulo: 'Dimensionamento ainda não definido',
      mensagem: '',
    };
  }

  const percentual = ((kit / necessaria) - 1) * 100;

  if (compatibilidadeParcial || kit < necessaria) {
    return {
      status: 'subdimensionado',
      bloqueia: true,
      potenciaNecessaria: necessaria,
      potenciaKit: kit,
      percentual,
      titulo: 'Kit disponível insuficiente',
      mensagem:
        `A potência necessária é ${necessaria.toFixed(2)} kWp e o kit disponível possui ` +
        `${kit.toFixed(2)} kWp. Cadastre um kit compatível para continuar.`,
    };
  }

  if (kit > necessaria * (1 + limite)) {
    return {
      status: 'sobredimensionado',
      bloqueia: false,
      potenciaNecessaria: necessaria,
      potenciaKit: kit,
      percentual,
      titulo: 'Kit acima da faixa recomendada',
      mensagem:
        `A potência necessária é ${necessaria.toFixed(2)} kWp e o kit selecionado possui ` +
        `${kit.toFixed(2)} kWp (${percentual.toFixed(1)}% acima). Você pode utilizar este kit ` +
        'ou cadastrar uma alternativa mais próxima do dimensionamento.',
    };
  }

  return {
    status: 'adequado',
    bloqueia: false,
    potenciaNecessaria: necessaria,
    potenciaKit: kit,
    percentual,
    titulo: 'Kit adequado',
    mensagem: '',
  };
};

export const potenciaReferenciaBombeamento = (dimensionamento) => {
  const potCC = Number(dimensionamento?.potCC || 0);
  const potEnergia = Number(dimensionamento?.potenciaKwpNecessaria || 0);
  return Math.max(potCC, potEnergia);
};


export const calcularDiferencaPercentual = (potenciaNecessaria, potenciaKit) => {
  const necessaria = normalizarPotenciaKit(potenciaNecessaria);
  const kit = normalizarPotenciaKit(potenciaKit);
  if (!Number.isFinite(necessaria) || necessaria <= 0 || !Number.isFinite(kit)) return null;
  return ((kit / necessaria) - 1) * 100;
};

const mesmoId = (a, b) => {
  const ida = a?._id || a;
  const idb = b?._id || b;
  return ida && idb ? String(ida) === String(idb) : false;
};

const nivelAlternativa = (kit, fornecedorAtual, moduloAtual) => {
  const mesmoFornecedor = mesmoId(kit?.fornecedor, fornecedorAtual);
  const mesmoModulo = mesmoId(kit?.modulo, moduloAtual);

  if (mesmoFornecedor && mesmoModulo) return 0;
  if (mesmoFornecedor) return 1;
  if (mesmoModulo) return 2;
  return 3;
};

export const buscarAlternativasKit = ({
  kits = [],
  potenciaNecessaria,
  fornecedorAtual,
  moduloAtual,
  tipo = 'ongrid',
  autonomiaNecessaria = 0,
  dod = 0.9,
  rte = 0.9,
  potenciaMotorCV = 0,
  limite = LIMITE_SOBREDIMENSIONAMENTO,
}) => {
  const necessaria = normalizarPotenciaKit(potenciaNecessaria);
  if (!Number.isFinite(necessaria) || necessaria <= 0) return [];

  return (Array.isArray(kits) ? kits : [])
    .filter((kit) => {
      if (!kit || kit.ativo === false) return false;

      if (tipo === 'ongrid') {
        if (kit.tipokat === 'bombeamento' || kit.tipokat === 'offgrid') return false;
      }

      if (tipo === 'bombeamento' && kit.tipokat && kit.tipokat !== 'bombeamento') {
        return false;
      }

      if (tipo === 'offgrid') {
        const autonomiaBruta =
          Number(kit?.autonomia || 0) ||
          (Number(kit?.bateria?.autonomia || 0) * Number(kit?.bateria?.qtde || 1));

        const autonomiaLiquida =
          autonomiaBruta *
          Number(dod || 0.9) *
          Number(rte || 0.9);

        if (autonomiaLiquida + 1e-9 < Number(autonomiaNecessaria || 0)) return false;
      }

      if (tipo === 'bombeamento') {
        const motorCV = Number(potenciaMotorCV || 0);
        const potenciaInversorCV =
          Number(kit?.potenciainversor || 0) ||
          (Number(kit?.inversor?.potencia || 0) * Number(kit?.quantidadeinversores || 1));

        if (motorCV > 0 && potenciaInversorCV + 1e-9 < motorCV) return false;
      }

      const pot = normalizarPotenciaKit(kit.potenciacc);
      return Number.isFinite(pot) && pot + 1e-9 >= necessaria;
    })
    .map((kit) => {
      const potenciaKit = normalizarPotenciaKit(kit.potenciacc);
      const adequacao = classificarAdequacaoKit({
        potenciaNecessaria: necessaria,
        potenciaKit,
        limite,
      });
      return {
        ...kit,
        _adequacao: adequacao,
        _nivelAlternativa: nivelAlternativa(kit, fornecedorAtual, moduloAtual),
        _autonomiaLiquida:
          tipo === 'offgrid'
            ? (
                (
                  Number(kit?.autonomia || 0) ||
                  (Number(kit?.bateria?.autonomia || 0) * Number(kit?.bateria?.qtde || 1))
                ) *
                Number(dod || 0.9) *
                Number(rte || 0.9)
              )
            : null,
        _potenciaInversorCV:
          tipo === 'bombeamento'
            ? (
                Number(kit?.potenciainversor || 0) ||
                (Number(kit?.inversor?.potencia || 0) * Number(kit?.quantidadeinversores || 1))
              )
            : null,
      };
    })
    .sort((a, b) => {
      if (a._nivelAlternativa !== b._nivelAlternativa) {
        return a._nivelAlternativa - b._nivelAlternativa;
      }
      const aSobre = a._adequacao?.status === 'sobredimensionado' ? 1 : 0;
      const bSobre = b._adequacao?.status === 'sobredimensionado' ? 1 : 0;
      if (aSobre !== bSobre) return aSobre - bSobre;
      return Number(a.potenciacc || 0) - Number(b.potenciacc || 0);
    });
};
